import { ref } from 'vue'

export type ConfirmOptions = {
  /** What the confirm button says; "Confirm" when absent. */
  confirmLabel?: string
  /** `danger` (the default) for a delete; `primary` for a question that is not a loss. */
  tone?: 'danger' | 'primary'
}

type ConfirmState = {
  message: string
  options: ConfirmOptions
  resolve: (value: boolean) => void
}

const state = ref<ConfirmState | null>(null)

/**
 * The app's "are you sure?", as a promise: `await confirm('…')` resolves true or false.
 * It replaces `ElMessageBox.confirm`, which would put element-plus back in the slices.
 *
 * The dialog it drives is `ConfirmDialogHost`, and the state behind it is one module-level
 * singleton — so **the app must render exactly one `<ConfirmDialogHost />`** or the
 * question is asked into nothing and the promise hangs until some later `confirm`
 * supersedes it (which answers this one "no"). The app shell mounts it; a second one
 * anywhere is the same bug as none.
 *
 * A CRUD table's delete already goes through this inside `useEntityTable.onDelete`; call
 * it yourself only for a confirmation that hook does not own.
 *
 * `accept` and `cancel` belong to the host. A caller wants `confirm` alone.
 */
export function useConfirm() {
  function confirm(message: string, options: ConfirmOptions = {}) {
    return new Promise<boolean>((resolve) => {
      // One singleton holds the pending question, so a second one asked before the first is
      // answered has to settle it here — an unsettled promise would suspend its caller for
      // the life of the page. "No" is the safe reading: nobody ever saw that dialog.
      state.value?.resolve(false)

      state.value = {
        message,
        options,
        resolve
      }
    })
  }

  function accept() {
    state.value?.resolve(true)
    state.value = null
  }

  function cancel() {
    state.value?.resolve(false)
    state.value = null
  }

  return {
    state,
    confirm,
    accept,
    cancel
  }
}
